import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
  MarketComponentMarketComponentIO,
  MarketComponentMarketComponentStatusDateInput,
} from '../../generated/fresh-guard-api';
import { queryKeys } from '../query-keys';

export function useMarketComponentUpdateStatusDateOptimistic() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      marketComponentId: string;
      marketComponentMarketComponentStatusDateInput: MarketComponentMarketComponentStatusDateInput;
    }) => {
      const res = await api.marketComponentApi.apiMarketComponentsMarketComponentIdPut(
        input.marketComponentId,
        input.marketComponentMarketComponentStatusDateInput
      );
      return res.data;
    },
    onMutate: async ({ marketComponentId, marketComponentMarketComponentStatusDateInput }) => {
      await qc.cancelQueries({
        queryKey: queryKeys.marketComponents.detail(marketComponentId),
      });

      const previousDetail = qc.getQueryData<MarketComponentMarketComponentIO>(
        queryKeys.marketComponents.detail(marketComponentId)
      );

      if (previousDetail) {
        qc.setQueryData<MarketComponentMarketComponentIO>(
          queryKeys.marketComponents.detail(marketComponentId),
          {
            ...previousDetail,
            lastValidation: marketComponentMarketComponentStatusDateInput.date ?? null,
          }
        );
      }

      return { previousDetail };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previousDetail) {
        qc.setQueryData(
          queryKeys.marketComponents.detail(vars.marketComponentId),
          ctx.previousDetail
        );
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.marketComponents.detail(vars.marketComponentId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.marketComponents.all });
    },
    meta: {
      skipGlobalError: false,
    },
  });
}
